import { Suspense, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Copy, Minus, Square, X } from 'lucide-react';
import type { AppId, WindowFrame } from '@omnia/shared';
import { tr } from '../lib/i18n.ts';
import { APPS } from './registry.ts';
import { AppBoundary, AppFallback } from './AppBoundary.tsx';
import { WindowContext } from './launch.ts';
import { ConfirmDiscard } from './ConfirmDiscard.tsx';
import { ownsGesture, resizeFrame, type Edge } from './gesture.ts';
import { GRID, clampFrame, snapFrame, snapToGrid, useDesktop, type SnapZone, type WindowState } from './store.ts';

const EDGES: Edge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
const SNAP_THRESHOLD = 12;

interface Gesture {
  kind: 'move' | 'resize';
  edge?: Edge;
  pointerId: number;
  pointerX: number;
  pointerY: number;
  start: WindowFrame;
  /** Title-bar grab point as a fraction of width, so un-maximizing keeps the window under the cursor. */
  grabRatio: number;
  detached: boolean;
  /** Mirrors of render state: pointerup can fire before React commits the last move. */
  draft: WindowFrame | null;
  snapZone: SnapZone | null;
}

export function Window({ id, state }: { id: AppId; state: WindowState }) {
  const app = APPS[id];
  const focused = useDesktop((s) => s.focused === id);
  const pendingOpen = useDesktop((s) => s.pendingOpen?.id === id);
  const { focus, close, minimize, toggleMaximize, setFrame, snap, setParams, setUnsaved, confirmPendingOpen, cancelPendingOpen } =
    useDesktop.getState();
  const [subtitle, setSubtitle] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const windowContext = useMemo(
    () => ({
      params: state.params,
      setParams: (params: typeof state.params) => setParams(id, params),
      setSubtitle,
      setUnsaved: (value: boolean) => setUnsaved(id, value),
      resize: (update: (size: { width: number; height: number }) => { width: number; height: number }) => {
        const { windows, bounds } = useDesktop.getState();
        const current = windows[id];
        // A maximized or snapped window is already sized by the user's layout choice.
        if (!current || current.restoreFrame) return;
        const { width, height } = update({ width: current.frame.width, height: current.frame.height });
        // Growing must not push the window past the desk's right or bottom edge.
        const x = Math.max(GRID, Math.min(current.frame.x, bounds.width - width - GRID));
        const y = Math.max(0, Math.min(current.frame.y, bounds.height - height - GRID));
        setFrame(id, { x, y, width, height });
      },
    }),
    [id, state.params, setParams, setUnsaved, setFrame],
  );

  function requestClose() {
    if (useDesktop.getState().unsaved[id]) setConfirmingClose(true);
    else close(id);
  }
  const [draft, setDraft] = useState<WindowFrame | null>(null);
  const [snapZone, setSnapZone] = useState<SnapZone | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const frame = draft ?? state.frame;
  const maximized = state.restoreFrame !== null;
  const Body = app.component;
  const titleId = `window-title-${id}`;

  function begin(event: ReactPointerEvent, kind: Gesture['kind'], edge?: Edge) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    focus(id);
    if (gesture.current) return;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    gesture.current = {
      kind,
      edge,
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      start: state.frame,
      grabRatio: (event.clientX - deskRect(event).left - state.frame.x) / state.frame.width,
      detached: !maximized,
      draft: null,
      snapZone: null,
    };
  }

  function move(event: ReactPointerEvent) {
    const g = gesture.current;
    if (!g || !ownsGesture(g, event.pointerId)) return;
    const dx = event.clientX - g.pointerX;
    const dy = event.clientY - g.pointerY;
    const bounds = useDesktop.getState().bounds;

    if (g.kind === 'resize' && g.edge) {
      g.draft = clampFrame(id, resizeFrame(g.start, g.edge, dx, dy, app.minWidth, app.minHeight), bounds);
      setDraft(g.draft);
      return;
    }

    let start = g.start;
    if (!g.detached) {
      // Dragging a snapped window restores its size first, anchored where the user grabbed it.
      if (Math.hypot(dx, dy) < 6) return;
      const restore = state.restoreFrame ?? g.start;
      start = { ...restore, x: g.start.x + g.start.width * g.grabRatio - restore.width * g.grabRatio, y: g.start.y };
      g.start = start;
      g.detached = true;
    }
    g.draft = clampFrame(id, { ...start, x: snapToGrid(start.x + dx), y: snapToGrid(start.y + dy) }, bounds);
    setDraft(g.draft);
    const desk = deskRect(event);
    const localX = event.clientX - desk.left;
    const localY = event.clientY - desk.top;
    g.snapZone =
      localY <= SNAP_THRESHOLD
        ? 'maximize'
        : localX <= SNAP_THRESHOLD
          ? 'left'
          : localX >= bounds.width - SNAP_THRESHOLD
            ? 'right'
            : null;
    setSnapZone(g.snapZone);
  }

  function end(event: ReactPointerEvent) {
    const g = gesture.current;
    if (!g || !ownsGesture(g, event.pointerId)) return;
    gesture.current = null;
    if (g.snapZone) snap(id, g.snapZone);
    else if (g.draft) setFrame(id, g.draft);
    setDraft(null);
    setSnapZone(null);
  }

  const ghost = snapZone ? snapFrame(snapZone, useDesktop.getState().bounds) : null;

  return (
    <>
      {ghost && <div className="snap-ghost" style={frameStyle(ghost)} aria-hidden />}
      {confirmingClose && (
        <ConfirmDiscard onDiscard={() => close(id)} onCancel={() => setConfirmingClose(false)} />
      )}
      {pendingOpen && !confirmingClose && (
        <ConfirmDiscard variant="replace" onDiscard={confirmPendingOpen} onCancel={cancelPendingOpen} />
      )}
      <section
        className="window"
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        data-focused={focused || undefined}
        data-maximized={maximized || undefined}
        data-gesture={draft ? 'active' : undefined}
        hidden={state.minimized}
        style={{ ...frameStyle(frame), zIndex: state.z, ['--tone' as string]: `var(${app.tone})` }}
        onPointerDownCapture={() => focus(id)}
      >
        <header
          className="titlebar"
          onPointerDown={(event) => begin(event, 'move')}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onDoubleClick={(event) => {
            if ((event.target as HTMLElement).closest('button')) return;
            toggleMaximize(id);
          }}
        >
          <span className="app-glyph" aria-hidden>
            <app.icon size={14} strokeWidth={2.25} />
          </span>
          <h2 id={titleId}>
            {app.title}
            {subtitle && <span className="window-subtitle"> — {subtitle}</span>}
          </h2>
          <div className="window-controls" onPointerDown={(event) => event.stopPropagation()}>
            <button type="button" aria-label={tr.window.minimize} onClick={() => minimize(id)}>
              <Minus size={14} />
            </button>
            <button
              type="button"
              aria-label={maximized ? tr.window.restore : tr.window.maximize}
              onClick={() => toggleMaximize(id)}
            >
              {maximized ? <Copy size={12} /> : <Square size={11} />}
            </button>
            <button type="button" className="control-close" aria-label={tr.window.close} onClick={requestClose}>
              <X size={14} />
            </button>
          </div>
        </header>
        <div className="window-body">
          <AppBoundary>
            <Suspense fallback={<AppFallback />}>
              <WindowContext value={windowContext}>
                <Body />
              </WindowContext>
            </Suspense>
          </AppBoundary>
        </div>
        {!maximized &&
          EDGES.map((edge) => (
            <div
              key={edge}
              className={`resize resize-${edge}`}
              aria-hidden
              onPointerDown={(event) => begin(event, 'resize', edge)}
              onPointerMove={move}
              onPointerUp={end}
              onPointerCancel={end}
            />
          ))}
      </section>
    </>
  );
}

function frameStyle(frame: WindowFrame) {
  return { transform: `translate3d(${frame.x}px, ${frame.y}px, 0)`, width: frame.width, height: frame.height };
}

function deskRect(event: ReactPointerEvent) {
  return (event.currentTarget as HTMLElement).closest('.desk')!.getBoundingClientRect();
}
